import { useMemo } from 'react';
import useService from '@modules/ServiceModule/useService';
import ServiceModule from '@modules/ServiceModule';
import { QueueRow } from './QueuesTable/types';

/**
 * Provides queue data for the outbound call modal sourced from RTCS via
 * `useService`. `number` resolves to `entrypoints[0]` from the service queue
 * data.
 *
 * When `useStrictAvailability` is true, `availableUsers` counts only agents
 * who can actually be offered a queue call (Available status AND `None` or
 * `Call-Offer` activity), matching the call center monitoring "Capacity"
 * widget. When false, it falls back to the legacy "set to Available"
 * definition.
 */
export const useServiceQueuesState = ({
  useStrictAvailability = false,
}: {
  useStrictAvailability?: boolean;
} = {}) => {
  const { queues, loading, userByContactCenterUserId } = useService();

  const mappedQueues: QueueRow[] = useMemo(
    () =>
      Object.values(queues).map((queue) => {
        const isAvailable = useStrictAvailability
          ? ServiceModule.isUserAvailableForCalls
          : ServiceModule.isUserAvailable;
        const availableUsers = queue.memberIds.filter((memberId) => {
          const user = userByContactCenterUserId[memberId];
          return user ? isAvailable(user) : false;
        }).length;

        return {
          id: queue.id,
          name: queue.name,
          number: queue.entrypoints?.[0] ?? '',
          availableUsers,
          estimatedWait: queue.status?.estimatedWait,
        };
      }),
    [queues, userByContactCenterUserId, useStrictAvailability],
  );

  return { queues: mappedQueues, loading };
};
